import React from 'react'
import type { PublicTag } from 'apollo-gql'
import { useTranslation } from 'lib/i18n'

import Breadcrumbs, {
  BreadcrumbHorizontalPadding,
  BreadcrumbItem,
} from 'components/Breadcrumbs/Breadcrumbs'

const buildBreadcrumbsFor = (
  tag: PublicTag,
  items: BreadcrumbItem[] = [],
): BreadcrumbItem[] => {
  if (!tag) {
    return items
  }

  const { parentTag, profileCollection, visibleInNavigation, id } = tag
  const profileCollectionSlug = profileCollection?.slug
  const profileCollectionContentTitle = profileCollection?.publicCmsItem?.title

  if (
    profileCollectionSlug &&
    visibleInNavigation &&
    profileCollectionContentTitle
  ) {
    items.push({
      key: id,
      title: profileCollectionContentTitle,
      href: `/profiles/${profileCollectionSlug}`,
    })
  }

  if (!parentTag) {
    return items.reverse()
  }

  return buildBreadcrumbsFor(parentTag, items)
}

type TagsBreadcrumbsProps = {
  tag: PublicTag
  lastItemClickable?: boolean
  lastBreadcrumb?: BreadcrumbItem
  paddingHorizontal?: BreadcrumbHorizontalPadding
}

const TagsBreadcrumbs = ({
  tag,
  lastItemClickable = false,
  lastBreadcrumb,
  paddingHorizontal,
}: TagsBreadcrumbsProps): JSX.Element => {
  const { t } = useTranslation()
  const placeholderBreadcrumb = {
    key: 'all',
    title: t('TagsBreadcrumbs.placeholder', 'All'),
    href: `/search`,
  }

  const breadcrumbsItems = [
    placeholderBreadcrumb,
    ...buildBreadcrumbsFor(tag),
    lastBreadcrumb,
  ].filter(Boolean)

  return (
    <Breadcrumbs
      items={breadcrumbsItems}
      design="secondary"
      lastItemClickable={lastItemClickable}
      paddingHorizontal={paddingHorizontal}
    />
  )
}

export default TagsBreadcrumbs
